import { useState, useCallback, useEffect, useRef } from 'react'
import { X } from 'lucide-react'
import { CARD_STYLE, HEADER_STYLE, HEADER_TITLE, BODY_STYLE, FOOTER_STYLE, ICON_BTN } from './draggable-shell-styles'
import { useT } from '@/lib/i18n'

// ── Backdrop ─────────────────────────────────────────────────────────────────
// zIndex par défaut (10000) inchangé pour les usages hors groupes tbsUserMessages (ex. preview
// MultiFileField) ; les popups groupées (UserMessages.jsx) passent leur propre palier.
export function Backdrop({ zIndex = 10000 }) {
  return <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex }} />
}

// ── ResizeHandle ─────────────────────────────────────────────────────────────
const RESIZE_CURSORS = { n:'ns-resize', s:'ns-resize', e:'ew-resize', w:'ew-resize', ne:'ne-resize', se:'se-resize', sw:'sw-resize', nw:'nw-resize' }

function ResizeHandle({ dir, onStart }) {
  const S = 8
  const pos = {}
  if (dir.includes('n')) pos.top = 0; else if (dir.includes('s')) pos.bottom = 0
  if (dir.includes('e')) pos.right = 0; else if (dir.includes('w')) pos.left = 0
  if (dir === 'n' || dir === 's') { pos.left = S; pos.right = S; pos.height = S }
  if (dir === 'e' || dir === 'w') { pos.top = S; pos.bottom = S; pos.width = S }
  if (dir.length === 2) { pos.width = S * 2; pos.height = S * 2 }
  return (
    <div style={{ position: 'absolute', zIndex: 2, cursor: RESIZE_CURSORS[dir], ...pos }}
      onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onStart(e, dir) }} />
  )
}

// Largeur/hauteur en pixels — tolère un nombre, une chaîne numérique ("400") ou une valeur CSS
// explicite ("400px") : la math de drag/resize a besoin d'un nombre, contrairement à
// toCssLength() (src/lib/utils.js) qui produit une chaîne CSS. Retombe sur `fallback` si absent
// ou invalide.
function toPx(value, fallback) {
  const n = typeof value === 'number' ? value : parseFloat(value)
  return Number.isFinite(n) ? n : fallback
}

// ── DraggableShell — boîte déplaçable / redimensionnable / maximisable ─────────
// Partagée par PromptHtmlEditor/PromptACEEditor/popupInplace/modalDialogBox (UserMessages.jsx) et
// la prévisualisation de fichier (MultiFileField, type 19).
// bodyStyle : override du style de la zone contenu (ex: { padding: 0 } pour iframe/image plein)
// zIndex : palier du groupe tbsUserMessages (10001 par défaut, hors groupes — ex. preview
// MultiFileField) ; le backdrop se place systématiquement 1 en dessous.
export function DraggableShell({ title, onClose, footer, children, initW = 700, initH = 500, initMaximized = false, bodyStyle = {}, zIndex = 10001 }) {
  const t = useT()
  const [maximized, setMaximized] = useState(!!initMaximized)
  const [size,  setSize]  = useState(() => ({
    w: Math.min(toPx(initW, 700), window.innerWidth),
    h: Math.min(toPx(initH, 500), window.innerHeight),
  }))
  const [posXY, setPosXY] = useState(null)
  const [dragging, setDragging] = useState(false)

  // Refs synchrones — besoin de lire taille/position à jour depuis le handler `resize` de la
  // fenêtre sans le réabonner à chaque drag/resize (évite de dépendre de `size`/`posXY` dans le effect).
  const sizeRef = useRef(size)
  const posRef  = useRef(posXY)
  const setSizeSync = next => { sizeRef.current = next; setSize(next) }
  const setPosSync  = next => { posRef.current = next; setPosXY(next) }

  const resolvePos = useCallback(() =>
    posXY ?? { x: Math.max(0, (window.innerWidth - size.w) / 2), y: Math.max(0, (window.innerHeight - size.h) / 2) }
  , [posXY, size])

  // Recadre la boîte si elle dépasse la fenêtre (ouverture avec une taille trop grande, ou
  // redimensionnement du navigateur après coup) — sans ce listener, une iframe/un contenu plus
  // grand que le viewport resterait coupé sans ascenseur ni moyen de l'atteindre.
  useEffect(() => {
    if (maximized) return
    const onResize = () => {
      const maxW = window.innerWidth, maxH = window.innerHeight
      const prevSize = sizeRef.current
      const w = Math.min(prevSize.w, maxW), h = Math.min(prevSize.h, maxH)
      if (w !== prevSize.w || h !== prevSize.h) setSizeSync({ w, h })

      const prevPos = posRef.current
      if (prevPos) {
        const x = Math.min(Math.max(0, prevPos.x), Math.max(0, maxW - w))
        const y = Math.min(Math.max(0, prevPos.y), Math.max(0, maxH - h))
        if (x !== prevPos.x || y !== prevPos.y) setPosSync({ x, y })
      }
    }
    window.addEventListener('resize', onResize)
    onResize()
    return () => window.removeEventListener('resize', onResize)
  }, [maximized])

  const makeDrag = (sx, sy, sp, onMove) => {
    setDragging(true)
    const move = ev => onMove(ev)
    const up   = () => { setDragging(false); document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up) }
    document.addEventListener('mousemove', move)
    document.addEventListener('mouseup', up)
  }

  const startDrag = e => {
    if (maximized) return
    e.preventDefault()
    const sx = e.clientX, sy = e.clientY, sp = resolvePos()
    makeDrag(sx, sy, sp, ev => setPosSync({ x: sp.x + (ev.clientX - sx), y: sp.y + (ev.clientY - sy) }))
  }

  const startResize = (e, dir) => {
    if (maximized) return
    e.preventDefault()
    const sx = e.clientX, sy = e.clientY, sw = size.w, sh = size.h, sp = resolvePos()
    makeDrag(sx, sy, sp, ev => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy
      let nw = sw, nh = sh, nx = sp.x, ny = sp.y
      if (dir.includes('e')) nw = Math.min(Math.max(400, sw + dx), window.innerWidth - sp.x)
      if (dir.includes('s')) nh = Math.min(Math.max(300, sh + dy), window.innerHeight - sp.y)
      if (dir.includes('w')) { nw = Math.max(400, sw - dx); nx = sp.x + sw - nw; if (nx < 0) { nw += nx; nx = 0 } }
      if (dir.includes('n')) { nh = Math.max(300, sh - dy); ny = sp.y + sh - nh; if (ny < 0) { nh += ny; ny = 0 } }
      setSizeSync({ w: nw, h: nh }); setPosSync({ x: nx, y: ny })
    })
  }

  const pos = posXY ?? { x: Math.max(0, (window.innerWidth - size.w) / 2), y: Math.max(0, (window.innerHeight - size.h) / 2) }
  const boxStyle = maximized
    ? { position:'fixed', left:0, top:0, width:'100vw', height:'100vh' }
    : { position:'fixed', left:pos.x, top:pos.y, width:size.w, height:size.h }

  return (
    <>
      <Backdrop zIndex={zIndex - 1} />
      <div style={{
        zIndex, ...boxStyle, ...CARD_STYLE,
        borderRadius: maximized ? 0 : 'var(--radius)',
        animation: 'tbs-pop-in .15s ease',
      }}>
        {!maximized && ['n','s','e','w','ne','se','sw','nw'].map(d => (
          <ResizeHandle key={d} dir={d} onStart={startResize} />
        ))}
        {/* Title bar */}
        <div onMouseDown={startDrag} onDoubleClick={() => setMaximized(m => !m)}
          style={{ ...HEADER_STYLE, cursor: maximized ? 'default' : 'move', userSelect: 'none' }}>
          <span style={HEADER_TITLE}>{title}</span>
          <button type="button" title={maximized ? 'Restaurer' : 'Maximiser'} style={ICON_BTN}
            onClick={e => { e.stopPropagation(); setMaximized(m => !m) }}>
            <i className={maximized ? 'fa-regular fa-window-restore' : 'fa-regular fa-window-maximize'} style={{ fontSize: 13 }} />
          </button>
          <button type="button" title={t('close')} style={ICON_BTN}
            onClick={e => { e.stopPropagation(); onClose() }}>
            <X size={14} />
          </button>
        </div>
        {/* Body */}
        <div style={{ ...BODY_STYLE, position: 'relative', ...bodyStyle, pointerEvents: dragging ? 'none' : 'auto' }}>{children}</div>
        {/* Footer */}
        {footer && <div style={FOOTER_STYLE}>{footer}</div>}
      </div>
    </>
  )
}
